import {Box, Card, Loader} from '@mantine/core';
import {WhiteBgButton} from '@packages/ui';
import {format, subDays} from 'date-fns';
import type React from 'react';
import {useState} from 'react';
import {MdArrowForwardIos, MdArrowLeft, MdArrowRight} from 'react-icons/md';
import {COLOR_BLACK_2} from '../../../config/constants/colorCodes';
import {
  MAX_COMPANY_PER_RANKING,
  TAB_LABELS,
} from '../../../config/constants/followCompanyRanking';
import {COMPANIES} from '../../../config/constants/routes';
import {FOLLOW_COMPANY_RANKING_TERMS} from '../../../core/entities/company/constants';
import type {FollowRankingListTermType} from '../../../core/entities/company/schema';
import {useGetFollowCompanyRankingList} from '../../../core/repositories/companyRepository';
import {CommonLink} from '../CommonLink';
import {RenderRankingNumber} from './RenderRankingNumber';

export const FollowCompanyRanking: React.FC = () => {
  const [currentTerm, setCurrentTerm] = useState<FollowRankingListTermType>(
    FOLLOW_COMPANY_RANKING_TERMS.daily,
  );

  const {data: rankingList, isLoading: isLoadingRankingList} =
    useGetFollowCompanyRankingList({
      limit: MAX_COMPANY_PER_RANKING,
      term:
        currentTerm === FOLLOW_COMPANY_RANKING_TERMS.all
          ? undefined
          : currentTerm,
    });

  const changePrevTerm = () => {
    switch (currentTerm) {
      case FOLLOW_COMPANY_RANKING_TERMS.daily: {
        setCurrentTerm(FOLLOW_COMPANY_RANKING_TERMS.all);
        break;
      }
      case FOLLOW_COMPANY_RANKING_TERMS.weekly: {
        setCurrentTerm(FOLLOW_COMPANY_RANKING_TERMS.daily);
        break;
      }
      case FOLLOW_COMPANY_RANKING_TERMS.monthly: {
        setCurrentTerm(FOLLOW_COMPANY_RANKING_TERMS.weekly);
        break;
      }
      case FOLLOW_COMPANY_RANKING_TERMS.all: {
        setCurrentTerm(FOLLOW_COMPANY_RANKING_TERMS.monthly);
        break;
      }
      default: {
        break;
      }
    }
  };

  const changeNextTerm = () => {
    switch (currentTerm) {
      case FOLLOW_COMPANY_RANKING_TERMS.daily: {
        setCurrentTerm(FOLLOW_COMPANY_RANKING_TERMS.weekly);
        break;
      }
      case FOLLOW_COMPANY_RANKING_TERMS.weekly: {
        setCurrentTerm(FOLLOW_COMPANY_RANKING_TERMS.monthly);
        break;
      }
      case FOLLOW_COMPANY_RANKING_TERMS.monthly: {
        setCurrentTerm(FOLLOW_COMPANY_RANKING_TERMS.all);
        break;
      }
      case FOLLOW_COMPANY_RANKING_TERMS.all: {
        setCurrentTerm(FOLLOW_COMPANY_RANKING_TERMS.daily);
        break;
      }
      default: {
        break;
      }
    }
  };

  return (
    <Card className="w-full bg-white px-8 py-16">
      <Box className="max-auto flex flex-col items-center">
        <Box className="flex w-full flex-row items-center justify-between">
          <Box
            onClick={() => {
              changePrevTerm();
            }}
            className="cursor-pointer"
          >
            <MdArrowLeft size={36} />
          </Box>
          <Box className="text-sm  text-black-2">フォロー数ランキング</Box>
          <Box
            onClick={() => {
              changeNextTerm();
            }}
            className="cursor-pointer"
          >
            <MdArrowRight size={36} />
          </Box>
        </Box>
        <Box className="mx-auto text-base font-bold text-black-2">
          {TAB_LABELS[currentTerm]} {getTermDate(currentTerm)}
        </Box>
        {isLoadingRankingList && (
          <div className="flex justify-center items-center w-full py-64">
            <Loader color="gray" />
          </div>
        )}

        {(rankingList?.ok ? rankingList.value.ranking : []).map(
          (item, index) => (
            <CommonLink
              className="flex w-[90%] flex-row items-center border-b-2 border-b-line px-8"
              href={`${COMPANIES}/${item.company.id}`}
              key={index}
              target="_blank"
            >
              {RenderRankingNumber(item.rank)}
              <Box className="ml-8 w-[55%] text-base text-primary-2">
                {item.company.name}
              </Box>
              <Box className="ml-16 w-[50px] text-right text-base text-black-2">
                +{item.diff}
              </Box>
              <MdArrowForwardIos color={COLOR_BLACK_2} />
            </CommonLink>
          ),
        )}

        {rankingList?.ok && rankingList?.value.ranking.length === 0 && (
          <Box>
            <Box className="mx-8 my-16 text-base text-black-2">
              フォローされた企業がありません。
            </Box>
            <WhiteBgButton
              className="my-8 w-full"
              onClick={() => {
                location.href = '/mypage/follow_companies';
              }}
              label="フォロー企業を見る"
              isPrimary={false}
              isLarge={false}
            />
          </Box>
        )}
      </Box>
    </Card>
  );
};

const getTermDate = (term: string): string => {
  switch (term) {
    case FOLLOW_COMPANY_RANKING_TERMS.daily:
      return `(${format(new Date(), 'MM/dd')})`;
    case FOLLOW_COMPANY_RANKING_TERMS.weekly:
      return `(${format(subDays(new Date(), 7), 'MM/dd')} ~ ${format(new Date(), 'MM/dd')})`;
    case FOLLOW_COMPANY_RANKING_TERMS.monthly:
      return `(${format(subDays(new Date(), 30), 'MM/dd')} ~ ${format(new Date(), 'MM/dd')})`;
    default:
      return '';
  }
};
